import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Edit3, MessageCircle, Phone, RefreshCw, Search, Trash2, Upload, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["New", "Contacted", "Follow-up", "Visited", "Member", "Lost"];

const STATUS_STYLES = {
  New: "bg-brand/10 text-brand border-brand/30",
  Contacted: "bg-blue-50 text-blue-600 border-blue-200",
  "Follow-up": "bg-amber-50 text-amber-600 border-amber-200",
  Visited: "bg-violet-50 text-violet-600 border-violet-200",
  Member: "bg-emerald-50 text-emerald-600 border-emerald-200",
  Lost: "bg-slate-100 text-slate-500 border-slate-200",
};

const waNumber = (p) => {
  const d = (p || "").replace(/\D/g, "");
  if (d.length === 10) return "91" + d;
  if (d.length === 11 && d.startsWith("0")) return "91" + d.slice(1);
  if (d.length === 12 && d.startsWith("91")) return d;
  return d;
};

export default function LeadsManager({ clubScope = null, canDelete = true }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");
  const [club, setClub] = useState("All");
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);

  const load = () => axios.get(`${API}/admin/enquiries`, { withCredentials: true }).then(({ data }) => setRows(data)).catch(() => toast.error("Could not load leads"));
  useEffect(() => { load(); }, []);

  const clubs = useMemo(() => [...new Set((rows || []).map((r) => r.club).filter(Boolean))], [rows]);
  const counts = useMemo(() => {
    const c = { All: (rows || []).length };
    STATUSES.forEach((s) => (c[s] = (rows || []).filter((r) => (r.status || "New") === s).length));
    return c;
  }, [rows]);

  const filtered = (rows || []).filter((r) => {
    if (status !== "All" && (r.status || "New") !== status) return false;
    if (club !== "All" && r.club !== club) return false;
    if (q) {
      const hay = `${r.name} ${r.phone} ${r.email} ${r.club} ${r.message}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  const setLeadStatus = async (id, val) => {
    try {
      const { data } = await axios.put(`${API}/admin/enquiries/${id}`, { status: val }, { withCredentials: true });
      setRows((rs) => rs.map((r) => (r.id === data.id ? data : r)));
      toast.success(`Marked ${val}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await axios.delete(`${API}/admin/enquiries/${id}`, { withCredentials: true });
      toast.success("Lead deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const downloadCsv = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/enquiries/export`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `oneiros-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Leads downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  const importLeads = async () => {
    const lines = importText.split("\n").map((l) => l.trim()).filter(Boolean);
    const leads = [];
    for (const line of lines) {
      const parts = line.split(",").map((p) => p.trim());
      if (parts[0] && parts[1]) leads.push({ name: parts[0], phone: parts[1], email: parts[2] || "", club: parts[3] || "", message: parts.slice(4).join(", ") || "" });
    }
    if (!leads.length) {
      toast.error("No valid rows — each line needs at least: Name, Phone");
      return;
    }
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/admin/enquiries/import`, { leads }, { withCredentials: true });
      toast.success(`${data.added} lead${data.added === 1 ? "" : "s"} imported`);
      setImportOpen(false);
      setImportText("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/enquiries/${edit.id}`,
        { name: edit.name, email: edit.email, phone: edit.phone, club: edit.club, message: edit.message, status: edit.status, notes: edit.notes },
        { withCredentials: true }
      );
      setRows((rs) => rs.map((r) => (r.id === data.id ? data : r)));
      setEdit(null);
      toast.success("Lead updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const input = "w-full border border-hairline px-4 py-3 text-sm outline-none focus:border-brand";
  const waLink = (r) => `https://wa.me/${waNumber(r.phone)}?text=${encodeURIComponent(`Hi ${r.name}, this is Oneiros Sports Club Surat regarding your enquiry. When is a good time to call?`)}`;

  return (
    <div data-testid="admin-leads-panel" className="border border-hairline bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline px-6 py-4">
        <h2 className="font-display text-2xl text-navy">Leads</h2>
        <div className="flex items-center gap-2">
          <button data-testid="admin-leads-import" onClick={() => setImportOpen(true)} className="inline-flex items-center gap-2 rounded-full border border-hairline px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-navy hover:border-brand hover:text-brand">
            <Upload className="h-3.5 w-3.5" /> Import
          </button>
          <button data-testid="admin-leads-download" onClick={downloadCsv} className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white hover:bg-navy-obsidian">
            <Download className="h-3.5 w-3.5" /> Download CSV
          </button>
          <button data-testid="admin-leads-refresh" onClick={load} className="inline-flex items-center gap-2 rounded-full border border-hairline px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-navy hover:border-brand hover:text-brand">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-hairline px-6 py-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-faint" />
          <input data-testid="admin-leads-search" placeholder="Search name, phone, email…" value={q} onChange={(e) => setQ(e.target.value)} className="w-56 border border-hairline py-2.5 pl-9 pr-3 text-sm outline-none focus:border-brand" />
        </div>
        {!clubScope && (
          <select data-testid="admin-leads-club-filter" value={club} onChange={(e) => setClub(e.target.value)} className="border border-hairline px-3 py-2.5 text-sm outline-none focus:border-brand">
            <option value="All">All clubs</option>
            {clubs.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <div className="flex flex-wrap gap-1.5">
          {["All", ...STATUSES].map((s) => (
            <button
              key={s}
              data-testid={`admin-leads-status-chip-${s.toLowerCase().replace(" ", "-")}`}
              onClick={() => setStatus(s)}
              className={`rounded-full border px-3.5 py-2 text-[10px] font-bold uppercase tracking-[0.14em] transition-colors ${status === s ? "border-navy bg-navy text-white" : "border-hairline text-mute hover:text-navy"}`}
            >
              {s} {counts[s] ? `(${counts[s]})` : ""}
            </button>
          ))}
        </div>
      </div>

      {!rows ? (
        <p className="px-6 py-10 text-center text-sm text-mute">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="px-6 py-10 text-center text-sm text-mute">No leads match — try clearing the search or filters.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-left text-sm">
            <thead>
              <tr className="border-b border-hairline text-[10px] uppercase tracking-[0.18em] text-faint">
                <th className="px-6 py-3">Date</th><th className="px-4 py-3">Lead</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Club</th><th className="px-4 py-3">Source</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`admin-lead-row-${r.id}`} className="border-b border-hairline/60 align-top">
                  <td className="whitespace-nowrap px-6 py-4 text-xs text-mute">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-4">
                    <p className="font-semibold text-navy">{r.name}</p>
                    <p className="text-xs text-mute">{r.email}</p>
                    {r.message && <p className="mt-1 max-w-[220px] text-xs text-mute/70">“{r.message.slice(0, 80)}{r.message.length > 80 ? "…" : ""}”</p>}
                    {r.notes && <p className="mt-1 max-w-[220px] text-xs italic text-brand/80">Note: {r.notes.slice(0, 80)}</p>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-4">
                    <p className="text-navy">{r.phone}</p>
                    <div className="mt-1.5 flex gap-1.5">
                      <a data-testid={`admin-lead-call-${r.id}`} href={`tel:${(r.phone || "").replace(/\s/g, "")}`} title="Call" className="inline-flex items-center gap-1 rounded-full border border-hairline px-2.5 py-1 text-[10px] font-bold text-navy hover:border-brand hover:text-brand">
                        <Phone className="h-3 w-3" /> Call
                      </a>
                      <a data-testid={`admin-lead-whatsapp-${r.id}`} href={waLink(r)} target="_blank" rel="noreferrer" title="WhatsApp" className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-bold text-emerald-600 hover:bg-emerald-100">
                        <MessageCircle className="h-3 w-3" /> WhatsApp
                      </a>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-xs">{r.club || "—"}</td>
                  <td className="px-4 py-4 text-xs text-mute">{r.source || "Website"}</td>
                  <td className="px-4 py-4">
                    <select
                      data-testid={`admin-lead-status-${r.id}`}
                      value={r.status || "New"}
                      onChange={(e) => setLeadStatus(r.id, e.target.value)}
                      className={`rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] outline-none ${STATUS_STYLES[r.status || "New"]}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex justify-end gap-1.5">
                      <button data-testid={`admin-lead-edit-${r.id}`} onClick={() => setEdit({ ...r, status: r.status || "New", notes: r.notes || "" })} title="Edit" className="border border-hairline px-2.5 py-2 text-navy hover:border-brand hover:text-brand">
                        <Edit3 className="h-3.5 w-3.5" />
                      </button>
                      {canDelete && (
                        <button data-testid={`admin-lead-delete-${r.id}`} onClick={() => remove(r.id)} title="Delete" className="border border-hairline px-2.5 py-2 text-navy hover:border-red-400 hover:text-red-500">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {importOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-navy/60 p-4" data-testid="admin-leads-import-modal">
          <div className="w-full max-w-lg bg-white p-8">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-2xl text-navy">Import Past Leads</h3>
              <button data-testid="admin-leads-import-close" onClick={() => setImportOpen(false)} className="text-mute hover:text-navy"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-mute">One lead per line: <span className="font-semibold text-navy">Name, Phone, Email, Club, Message</span> (email/club/message optional). Lines from your old list, WhatsApp or inbox exports all work.</p>
            <textarea
              data-testid="admin-leads-import-text"
              rows={8}
              placeholder={"Ramesh Patel, 9876543210, ramesh@gmail.com, Oneiros Katargam, Interested in annual\nPriya Shah, 9812345678, , Oneiros Varachha"}
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              className="mt-4 w-full resize-none border border-hairline p-4 font-mono text-xs outline-none focus:border-brand"
            />
            <button data-testid="admin-leads-import-save" disabled={importing} onClick={importLeads} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white hover:bg-brand-dark disabled:opacity-60">
              {importing ? "Importing…" : "Import Leads"}
            </button>
          </div>
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-navy/60 p-4" data-testid="admin-lead-edit-modal">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto bg-white p-8">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-2xl text-navy">Edit Lead</h3>
              <button data-testid="admin-lead-edit-close" onClick={() => setEdit(null)} className="text-mute hover:text-navy"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-6 grid gap-3">
              <input data-testid="admin-lead-edit-name" placeholder="Name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={input} />
              <div className="grid gap-3 sm:grid-cols-2">
                <input data-testid="admin-lead-edit-phone" placeholder="Phone" value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} className={input} />
                <input data-testid="admin-lead-edit-email" type="email" placeholder="Email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className={input} />
              </div>
              <input data-testid="admin-lead-edit-club" placeholder="Club" value={edit.club} disabled={!!clubScope} onChange={(e) => setEdit({ ...edit, club: e.target.value })} className={`${input} disabled:bg-mist disabled:text-mute`} />
              <select data-testid="admin-lead-edit-status" value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} className={input}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <textarea data-testid="admin-lead-edit-message" rows={3} placeholder="Message" value={edit.message} onChange={(e) => setEdit({ ...edit, message: e.target.value })} className={`${input} resize-none`} />
              <textarea data-testid="admin-lead-edit-notes" rows={2} placeholder="Your private notes (call outcome, follow-up…)" value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} className={`${input} resize-none`} />
              <button data-testid="admin-lead-edit-save" disabled={saving} onClick={saveEdit} className="inline-flex items-center justify-center gap-2 rounded-full bg-brand py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white hover:bg-brand-dark disabled:opacity-60">
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
